import { useState } from 'react';

import { appColors } from '@backrs_ui/common-ui';

import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  OfficeHoursMessage,
  LubavContact,
  SearchedUser,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, TextField, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { ResourceNugget } from './nuggets/ResourceNugget';

type UserSearchProps = Readonly<{
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void;
  asSuperuser?: boolean;
  networks?: string[];
  userId?: number;
  roles?: number[];
  sessionId?: number;
}>;

export const UserSearch = ({
  onClick,
  asSuperuser = false,
  networks,
  userId,
  sessionId,
  roles,
}: UserSearchProps) => {
  const [userInput, setUserInput] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<SearchedUser[] | null>(null);

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setUserSearchResults(null);

      return;
    }

    try {
      const response = await axios.get(`/api/search-for-user`, {
        params: {
          query,
          as_superuser: asSuperuser,
          user_id: userId,
          ...(networks && { networks }),
          ...(roles && { roles }),
        },
        paramsSerializer: {
          indexes: null, // converts from roles[0]=1&roles[1]=7 to roles=1&roles=7 since the api expects an array
        },
      });

      setUserSearchResults(response.data);
    } catch (err) {
      console.error('Error searching for users:', err);
      setUserSearchResults([]);
    }
  };

  const handleUserSelect = async (user: SearchedUser) => {
    await handleUserSelection(user.id.toString(), user.name, onClick, asSuperuser, sessionId);
    setUserSearchResults(null);
    setUserInput('');
  };

  return (
    <Box>
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        onKeyUp={() => handleSearch(userInput)}
        placeholder="Type to search for users by name"
        sx={textFieldStyles}
      />

      <Box sx={resultsContainerStyles}>
        {userInput && userSearchResults?.length === 0 && <Typography>No results found</Typography>}
        {userSearchResults?.map((user) => (
          <Box key={user.id} onClick={() => handleUserSelect(user)} sx={resultItemStyles}>
            <Typography>{user.name}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

type UserSelectProps = Readonly<{
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void;
  contacts: LubavContact[];
  asSuperuser?: boolean;
  sessionId?: number;
}>;

export const UserSelect = ({ contacts, onClick, asSuperuser, sessionId }: UserSelectProps) => {
  const handleContactClick = (contact: LubavContact) => {
    handleUserSelection(
      contact.id,
      contact.metadata.name,
      onClick,
      asSuperuser || false,
      sessionId,
    );
  };

  return (
    <>
      {contacts.map((contact) => (
        <Box key={contact.id} onClick={() => handleContactClick(contact)} sx={userSelectStyle}>
          <Box sx={{ width: '100%' }}>
            <ResourceNugget
              key={contact.id}
              type={contact.metadata.role === 'professional' ? 'backrs' : 'students'}
              resource={contact}
              disableExpansion={true}
            />
          </Box>
        </Box>
      ))}
    </>
  );
};

const handleUserSelection = async (
  userId: string,
  name: string,
  onClick: (
    message: string,
    LLMInput?: string,
    currentMessageList?: OfficeHoursMessage[],
    sessionId?: number,
  ) => void,
  asSuperuser: boolean,
  sessionId?: number,
) => {
  if (!sessionId) return;

  const profile = await axios
    .get(`/api/get-user-profile`, {
      params: { user_id: userId, as_superuser: asSuperuser },
    })
    .then((res) => res.data);

  onClick(
    `Check in on **${name}**`,
    `Provide a single paragraph summarizing my student ${name}, focusing on their current goals and undertakings.\n\n${profile}`,
    undefined,
    sessionId,
  );
};

const textFieldStyles: SxProps<Theme> = {
  marginBottom: '16px',
  mt: 3,
};

const resultsContainerStyles: SxProps<Theme> = {
  maxHeight: '300px',
  overflowY: 'auto',
};

const resultItemStyles: SxProps<Theme> = {
  padding: '8px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
};

const userSelectStyle: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '2px',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: appColors.color.blue1,
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
} as const;
